import React, { useState, useEffect, useCallback } from 'react';
import { firestore } from '../../firebase/config';
import { collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import EditModal from './EditModal';
import { useTodasLocalidades } from '../../hooks/useLocalidades';

// --- Função Auxiliar para Normalização ---
const normalizarTexto = (texto) => {
    if (!texto) return '';
    return String(texto).trim().toUpperCase();
};

const getTodayString = () => {
    const today = new Date();
    const offset = today.getTimezoneOffset();
    const todayLocal = new Date(today.getTime() - (offset * 60 * 1000));
    return todayLocal.toISOString().split('T')[0];
};

// Lista de Localidades carregada dinamicamente do Firestore (via useTodasLocalidades)

function HistoricoVisitas({ user }) {
    const { localidades: todasLocalidades } = useTodasLocalidades();
    const [startDate, setStartDate] = useState(getTodayString());
    const [endDate, setEndDate] = useState(getTodayString());

    // Função para calcular a data limite (máximo 7 dias de intervalo)
    const getMaxDate = (start) => {
        // Se for Master, sem limite de data
        if (user?.perfil === 'master') return '';

        if (!start) return '';
        const d = new Date(start);
        d.setDate(d.getDate() + 6); // 7 dias de intervalo (inclusive)
        return d.toISOString().split('T')[0];
    };

    // Handler para mudança na Data Inicial
    const handleStartDateChange = (e) => {
        const newStart = e.target.value;
        setStartDate(newStart);

        // Se for Master, não trava o calendário
        if (user?.perfil === 'master') return;

        // Se a data final atual for menor que a nova data inicial ou exceder o limite de 7 dias, ajusta automaticamente
        if (newStart && endDate) {
            const maxDate = getMaxDate(newStart);
            if (endDate < newStart) {
                setEndDate(newStart);
            } else if (endDate > maxDate) {
                setEndDate(maxDate);
            }
        }
    };
    const [visitas, setVisitas] = useState([]);
    const [loading, setLoading] = useState(false);
    const [agentsMap, setAgentsMap] = useState({});
    const [selectedVisita, setSelectedVisita] = useState(null);

    // Novos estados para filtros
    const [filterAgente, setFilterAgente] = useState('');
    const [filterQuarteirao, setFilterQuarteirao] = useState('');
    const [filterLocalidade, setFilterLocalidade] = useState('');
    const [showCanceladas, setShowCanceladas] = useState(false); // Toggle para mostrar canceladas


    // Busca agentes na montagem
    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const q = query(collection(firestore, "usuarios"), where("perfil", "==", "agente"));
                const querySnapshot = await getDocs(q);
                const agents = {};
                querySnapshot.docs.forEach(doc => {
                    const data = doc.data();
                    agents[doc.id] = data.nome || data.email;
                    if (data.email) agents[data.email.toLowerCase()] = data.nome || data.email;
                });
                setAgentsMap(agents);
            } catch (error) {
                console.error("Erro ao buscar agentes:", error);
            }
        };
        fetchAgents();
    }, []);

    const handleSearch = useCallback(async () => {
        // Validação de Localidade Obrigatória (Exceto Master)
        if (user?.perfil !== 'master' && !filterLocalidade) {
            alert("É obrigatório selecionar uma LOCALIDADE para realizar a busca.");
            return;
        }

        setLoading(true);
        try {
            const [sYear, sMonth, sDay] = startDate.split('-');
            const start = new Date(sYear, sMonth - 1, sDay);
            start.setHours(0, 0, 0, 0);

            const [eYear, eMonth, eDay] = endDate.split('-');
            const end = new Date(eYear, eMonth - 1, eDay);
            end.setHours(23, 59, 59, 999);
            
            // Validação de intervalo de 7 dias (Bypass e for Master)
            // Math.abs(end - start) onde end=23:59:59 e start=00:00:00 de uma semana inteira = ~6.9999 dias
            // Math.ceil(6.9999) = 7, portanto o limite correto é > 7 para permitir exatamente 7 dias (dom-sáb)
            const diffTime = Math.abs(end - start);
            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

            if (user?.perfil !== 'master' && diffDays > 7) { // Permite até 7 dias (domingo a sábado)
                alert("O período máximo de busca é de 7 dias para evitar sobrecarga no sistema.");
                setLoading(false);
                return;
            }

            let q;

            // Se tiver localidade selecionada, filtra no servidor (Otimizado)
            if (filterLocalidade) {
                const localidadeNormalizada = normalizarTexto(filterLocalidade);
                q = query(
                    collection(firestore, 'relatoriosDiarios_FAD1'),
                    where('dataAtividade', '>=', start),
                    where('dataAtividade', '<=', end),
                    where('localidade', '==', localidadeNormalizada),
                    orderBy('dataAtividade', 'desc')
                    // Sem limit(100) aqui, pois o filtro é específico
                );
            } else {
                // Se NÃO tiver localidade (apenas Master consegue fazer isso)
                // CORREÇÃO: Removido limit(100) para permitir visualizar todas as visitas
                q = query(
                    collection(firestore, 'relatoriosDiarios_FAD1'),
                    where('dataAtividade', '>=', start),
                    where('dataAtividade', '<=', end),
                    orderBy('dataAtividade', 'desc')
                    // Sem limite - Master pode ver todas as visitas do período
                );
            }

            const querySnapshot = await getDocs(q);
            let data = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            console.log("✅ Resultados encontrados no servidor:", querySnapshot.size);
            console.log("📊 Primeiros 3 resultados:", data.slice(0, 3).map(v => ({
                id: v.id,
                idVisita: v.idVisita,
                agente: v.emailAgente || v.idAgente,
                localidade: v.localidade,
                quarteirao: v.quarteirao,
                data: v.dataAtividade?.toDate?.().toLocaleDateString('pt-BR')
            })));

            // Aplica filtros RESTANTES no cliente (Agente, Quarteirão)
            // Nota: O filtro de localidade já foi aplicado no servidor se filterLocalidade existir,
            // mas mantê-lo aqui não faz mal (redundante mas seguro).
            if (filterAgente) {
                data = data.filter(v => {
                    const agenteKey = v.idAgente || v.emailAgente || '';
                    const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                    return normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente));
                });
            }

            if (filterQuarteirao) {
                data = data.filter(v => v.quarteirao && v.quarteirao.toString().includes(filterQuarteirao));
            }

            // Filtro cliente para garantir (caso haja inconsistência de normalização, embora devamos confiar no server)
            if (filterLocalidade) {
                data = data.filter(v => v.localidade && normalizarTexto(v.localidade).includes(normalizarTexto(filterLocalidade)));
            }

            // Filtro de Status de Registro (Canceladas)
            if (!showCanceladas) {
                // Por padrão, exclui visitas canceladas e duplicadas (Com robustez)
                data = data.filter(v => {
                    const s = v.statusRegistro ? v.statusRegistro.trim().toLowerCase() : 'ativo';
                    return s === 'ativo';
                });
            }



            console.log("🔍 Após filtros do cliente:", data.length, "visitas");

            setVisitas(data);
        } catch (error) {
            console.error("Erro ao buscar histórico:", error);
            alert("Erro ao buscar dados. Verifique o console.");
        }
        setLoading(false);
    }, [startDate, endDate, filterAgente, filterQuarteirao, filterLocalidade, agentsMap, user, showCanceladas]);


    // Refazer a busca automaticamente quando o toggle "Mostrar canceladas" for alterado
    useEffect(() => {
        if (visitas.length > 0) {
            handleSearch();
        }
    }, [showCanceladas, handleSearch]);

    // Busca inicial REMOVIDA para evitar carga desnecessária
    // useEffect(() => {
    //    handleSearch();
    // }, [handleSearch]);

    // --- Exportação Geral (FULL - Sem Limite) ---
    const handleExport = async () => {
        if (!startDate || !endDate) {
            alert("Selecione o intervalo.");
            return;
        }

        setLoading(true);
        try {
            const [sYear, sMonth, sDay] = startDate.split('-');
            const start = new Date(sYear, sMonth - 1, sDay);
            start.setHours(0, 0, 0, 0);

            const [eYear, eMonth, eDay] = endDate.split('-');
            const end = new Date(eYear, eMonth - 1, eDay);
            end.setHours(23, 59, 59, 999);

            // Query SEM LIMIT
            const q = query(
                collection(firestore, 'relatoriosDiarios_FAD1'),
                where('dataAtividade', '>=', start),
                where('dataAtividade', '<=', end),
                orderBy('dataAtividade', 'desc')
            );

            const querySnapshot = await getDocs(q);
            const rawData = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Aplica filtros do cliente
            const dataToExport = rawData.filter(v => {
                if (filterLocalidade) {
                    if (!v.localidade || normalizarTexto(v.localidade) !== normalizarTexto(filterLocalidade)) return false;
                }
                if (filterAgente) {
                    const agenteKey = v.idAgente || v.emailAgente || '';
                    const nomeAgente = agentsMap[agenteKey] || agentsMap[agenteKey?.toLowerCase?.()] || agenteKey;
                    if (!normalizarTexto(nomeAgente).includes(normalizarTexto(filterAgente))) return false;
                }
                if (filterQuarteirao) {
                    if (!v.quarteirao || !v.quarteirao.toString().includes(filterQuarteirao)) return false;
                }
                return true;
            });

            if (dataToExport.length === 0) {
                alert("Nada para exportar com os filtros atuais.");
                setLoading(false);
                return;
            }

            exportarExcel(dataToExport, `Historico_Gestor_${startDate}_${endDate}`);

        } catch (error) {
            console.error("Erro export:", error);
            alert("Erro ao exportar.");
        }
        setLoading(false);
    };

    const exportarExcel = (dados, fileName) => {
        let maxAmostras = 0;
        let maxTratFocal = 0;
        let maxTratPerifocal = 0;
        dados.forEach(v => {
            if (v.amostras && v.amostras.length > maxAmostras) maxAmostras = v.amostras.length;
            if (v.tratamentosFocais && v.tratamentosFocais.length > maxTratFocal) maxTratFocal = v.tratamentosFocais.length;
            if (v.tratamentosPerifocais && v.tratamentosPerifocais.length > maxTratPerifocal) maxTratPerifocal = v.tratamentosPerifocais.length;
        });

        const dataToSheet = dados.map(v => {
            let animaisStr = 'NÃO';
            if (v.temAnimais?.toUpperCase() === 'SIM') {
                animaisStr = v.animais?.map(a => `${a.tipo}(${a.sexo}):${a.quantidade}`).join('; ') || 'NÃO ESPECIFICADO';
            }

            const atividadeMap = { '1': 'RA', '2': 'LI+T', '3': 'PE', '4': 'T', '5': 'DF', '6': 'PVE', '7': 'ID', '8': 'BT' };
            let atividadeDetalhada = atividadeMap[v.atividade] || v.atividade;
            if (v.atividade === '2' && v.subAtividadeLIT) {
                const { li, t } = v.subAtividadeLIT;
                if (li && t) atividadeDetalhada = 'LI+T (Ambos)';
                else if (li) atividadeDetalhada = 'LI+T (Apenas LI)';
                else if (t) atividadeDetalhada = 'LI+T (Apenas T)';
            }

            const visitaRow = {
                'Data Visita': v.dataAtividade?.toDate ? v.dataAtividade.toDate().toLocaleString('pt-BR') : 'Data Inválida',
                'Agente': agentsMap[v.idAgente] || v.emailAgente,
                'Município': v.municipio,
                'Localidade': v.localidade,
                'Ciclo/Ano': v.cicloAno,
                'Zona': v.zona === '1' ? 'Urbana' : 'Rural',
                'Atividade': atividadeDetalhada,
                'Quarteirão': v.quarteirao,
                'Sequência': v.sequencia,
                'Logradouro': v.logradouro,
                'Nº Imóvel': v.numeroImovel,
                'Complemento': v.complemento,
                'Hora Entrada': v.horaEntrada,
                'Status Visita': v.visitaStatus,
                'Tipo Imóvel': v.tipoImovel,
                'Depósitos A1': v.depositos?.A1,
                'Depósitos A2': v.depositos?.A2,
                'Depósitos B': v.depositos?.B,
                'Depósitos C': v.depositos?.C,
                'Depósitos D1': v.depositos?.D1,
                'Depósitos D2': v.depositos?.D2,
                'Depósitos E': v.depositos?.E,
                'Depósitos Eliminados': v.depositoEliminado,
                'Coleta Amostra': v.coletaAmostra ? 'Sim' : 'Não',
                'Possui Animais': v.temAnimais?.toUpperCase() === 'SIM' ? 'Sim' : 'Não',
                'Detalhes dos Animais': animaisStr,
                'Latitude': v.coordenadas?.latitude,
                'Longitude': v.coordenadas?.longitude,
                'Tratamento Focal': v.tratFocalImovelTratado ? 'Sim' : 'Não',
            };

            for (let i = 0; i < maxTratFocal; i++) {
                const trat = v.tratamentosFocais?.[i];
                visitaRow[`Trat. Focal ${i + 1} - Depósito`] = trat?.depositoTratado || '';
                visitaRow[`Trat. Focal ${i + 1} - Tipo`] = trat?.tipoLarvicida || '';
                visitaRow[`Trat. Focal ${i + 1} - Gramas`] = trat?.qtdeGramas || 0;
            }

            visitaRow['Tratamento Perifocal'] = v.tratPerifocalImovelTratado ? 'Sim' : 'Não';
            for (let i = 0; i < maxTratPerifocal; i++) {
                const trat = v.tratamentosPerifocais?.[i];
                visitaRow[`Trat. Perifocal ${i + 1} - Tipo`] = trat?.tipoAdulticida || '';
                visitaRow[`Trat. Perifocal ${i + 1} - Gramas`] = trat?.qtdeGramas || 0;
            }

            if (v.coletaAmostra) {
                for (let i = 0; i < maxAmostras; i++) {
                    const amostra = v.amostras?.[i];
                    const analise = amostra?.analise;
                    visitaRow[`Amostra ${i + 1} - Depósito`] = amostra?.depositoColeta || '';
                    visitaRow[`Amostra ${i + 1} - Observação`] = amostra?.notaInicial || '';
                    visitaRow[`Amostra ${i + 1} - Status Análise`] = analise?.status || 'pendente';
                    visitaRow[`Amostra ${i + 1} - Aegypti (L)`] = analise?.aegypti?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (P)`] = analise?.aegypti?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (E)`] = analise?.aegypti?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Aegypti (A)`] = analise?.aegypti?.adultos || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (L)`] = analise?.albopictus?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (P)`] = analise?.albopictus?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (E)`] = analise?.albopictus?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Albopictus (A)`] = analise?.albopictus?.adultos || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (L)`] = analise?.outros?.larvas || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (P)`] = analise?.outros?.pupas || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (E)`] = analise?.outros?.exuvia || 0;
                    visitaRow[`Amostra ${i + 1} - Outros (A)`] = analise?.outros?.adultos || 0;
                }
            }

            return visitaRow;
        });

        const worksheet = XLSX.utils.json_to_sheet(dataToSheet);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Visitas");
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
    };

    const handleOpenModal = (visita) => setSelectedVisita(visita);
    const handleCloseModal = () => setSelectedVisita(null);
    const handleSaveSuccess = () => {
        setSelectedVisita(null);
        handleSearch();
    };

    return (
        <>
            <h3>Histórico de Visitas</h3>
            <div className="filters-container">
                <div className="input-group">
                    <label>Data de Início</label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={handleStartDateChange}
                    />
                </div>
                <div className="input-group">
                    <label>Data de Fim (Máx. 7 dias)</label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={e => setEndDate(e.target.value)}
                        min={startDate}
                        max={getMaxDate(startDate)}
                    />
                </div>

                {/* Filtros adicionais */}
                <div className="input-group">
                    <label>Filtrar Agente</label>
                    <input
                        type="text"
                        placeholder="Nome do agente..."
                        value={filterAgente}
                        onChange={e => setFilterAgente(e.target.value)}
                    />
                </div>
                <div className="input-group">
                    <label>Filtrar Quarteirão</label>
                    <input
                        type="text"
                        placeholder="Nº Quarteirão..."
                        value={filterQuarteirao}
                        onChange={e => setFilterQuarteirao(e.target.value)}
                    />
                </div>
                <div className="input-group">
                    <label>Filtrar Localidade {user?.perfil !== 'master' && <span style={{ color: 'red' }}>*</span>}</label>
                    <select
                        value={filterLocalidade}
                        onChange={e => setFilterLocalidade(e.target.value)}
                        required={user?.perfil !== 'master'}
                    >
                        <option value="">Selecione uma localidade</option>
                        {todasLocalidades.map(loc => (
                            <option key={loc.id} value={loc.nome}>{loc.nome}</option>
                        ))}
                    </select>
                </div>

                {/* Toggle para mostrar visitas canceladas */}
                <div className="input-group" style={{ display: 'flex', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '0.95em' }}>
                        <input
                            type="checkbox"
                            checked={showCanceladas}
                            onChange={(e) => setShowCanceladas(e.target.checked)}
                            style={{ cursor: 'pointer' }}
                        />
                        <span>Mostrar visitas canceladas</span>
                    </label>
                </div>

                <button onClick={handleSearch} disabled={loading}>
                    {loading ? 'Buscando...' : 'Buscar'}
                </button>
                <button onClick={handleExport} className="export-button" disabled={visitas.length === 0}>
                    Exportar para Excel
                </button>
            </div>

            {!loading && visitas.length > 0 && (
                <p style={{ marginTop: '20px', color: '#666' }}>
                    Total de visitas encontradas: <strong>{visitas.length}</strong>
                </p>
            )}

            <div className="table-wrapper">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>ID Visita</th>
                            <th>Data / Horário</th>
                            <th>Agente</th>
                            <th>Quarteirão</th>
                            <th>Localidade</th>
                            <th>Nº</th>
                            <th>Comp.</th>
                            <th>Status</th>
                            <th>Ações</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan="8">Carregando...</td></tr>
                        ) : visitas.length === 0 ? (
                            <tr><td colSpan="8">Nenhuma visita encontrada para o período selecionado.</td></tr>
                        ) : (
                            visitas.map(v => {
                                const statusNorm = v.statusRegistro ? v.statusRegistro.trim().toLowerCase() : 'ativo';
                                const isCancelado = statusNorm !== 'ativo';

                                return (
                                    <tr key={v.id} style={{ backgroundColor: isCancelado ? '#ffe6e6' : 'transparent' }}>
                                        <td>
                                            <strong>{v.idVisita || '-'}</strong>
                                            {isCancelado && (
                                                <span style={{
                                                    display: 'block',
                                                    fontSize: '0.75em',
                                                    color: '#dc3545',
                                                    fontWeight: 'bold',
                                                    marginTop: '3px'
                                                }}>
                                                    ⚠️ {v.statusRegistro ? v.statusRegistro.toUpperCase() : 'CANCELADO'}
                                                </span>
                                            )}
                                        </td>
                                        <td>{v.dataAtividade?.toDate ? v.dataAtividade.toDate().toLocaleString('pt-BR') : '—'}</td>

                                        <td>{agentsMap[v.idAgente] || v.emailAgente}</td>
                                        <td>{v.quarteirao}</td>
                                        <td>{v.localidade}</td>
                                        <td>{v.numeroImovel || 'N/A'}</td>
                                        <td>{v.complemento || '—'}</td>
                                        <td>
                                            <span className={`status status-${v.status?.replace(/\s+/g, '-').toLowerCase()}`}>
                                                {v.status || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <button onClick={() => handleOpenModal(v)} className="action-button">
                                                Visualizar / Editar
                                            </button>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {
                selectedVisita && (
                    <EditModal
                        visita={selectedVisita}
                        agentsMap={agentsMap}
                        onClose={handleCloseModal}
                        onSaveSuccess={handleSaveSuccess}
                        currentUser={user}
                    />
                )
            }
        </>
    );
}

export default HistoricoVisitas;